export const LIABILITY_TERMS = [
    {
        id: 'additional-insured',
        term: 'Additional insured',
        short: 'AI',
        category: 'liability',
        essential: true,
        def: 'Someone added to another party\'s liability policy so that policy defends and pays for them too — usually a landlord, a general contractor, or a customer who required it by contract.',
        detail: 'Which endorsement is used decides what they actually get. CG 20 10 covers ongoing operations only; CG 20 37 adds completed operations; the 04 13 editions limit coverage to what the contract required and to the extent permitted by law. A general contractor given only CG 20 10 has no coverage once the job is finished — which is when construction defect claims arrive.',
        confused: 'An additional insured is covered by the policy. A certificate holder is merely told the policy exists. A loss payee gets property money, not liability defence.',
        see: ['certificate-holder', 'loss-payee', 'primary-and-noncontributory', 'blanket-additional-insured', 'products-completed-operations'],
    },
    {
        id: 'blanket-additional-insured',
        term: 'Blanket additional insured',
        aka: ['automatic additional insured'],
        category: 'liability',
        def: 'An endorsement granting additional insured status automatically to anyone the insured has agreed in a written contract to add, without naming them.',
        detail: 'It is the practical answer for a contractor with fifty upstream parties. Read what triggers it: most require a written contract executed *before* the loss, and many exclude completed operations unless the contract expressly requires them.',
        see: ['additional-insured', 'insured-contract'],
    },
    {
        id: 'certificate-holder',
        term: 'Certificate holder',
        category: 'documents',
        essential: true,
        def: 'The person or company a certificate of insurance is issued to. It means they have been told about the coverage — nothing more.',
        detail: 'Being a certificate holder confers no rights under the policy and no right to notice of cancellation, whatever the certificate appears to say. If someone needs rights, they need an endorsement.',
        confused: 'Constantly mistaken for additional insured status. They are not related.',
        see: ['coi', 'additional-insured', 'notice-of-cancellation'],
    },
    {
        id: 'loss-payee',
        term: 'Loss payee',
        category: 'liability',
        def: 'A party named to receive payment for damage to specific property they have a financial interest in — usually a lender on equipment or a vehicle.',
        confused: 'A loss payee gets property claim money. An additional insured gets liability defence and indemnity. Lenders often ask for the wrong one.',
        see: ['mortgagee', 'additional-insured', 'insurable-interest'],
    },
    {
        id: 'mortgagee',
        term: 'Mortgagee',
        aka: ['mortgage holder'],
        category: 'property',
        def: 'The lender on real property, named on the property policy to receive building loss payments and cancellation notice.',
        detail: 'The standard mortgage clause gives the mortgagee rights the insured does not have: it can still be paid even if the insured\'s own claim is denied for an act of theirs, and it is entitled to its own notice of cancellation.',
        see: ['loss-payee', 'notice-of-cancellation'],
    },
    {
        id: 'primary-and-noncontributory',
        term: 'Primary and noncontributory',
        short: 'P&NC',
        category: 'liability',
        essential: true,
        def: 'A requirement that the insured\'s policy pays first and does not ask the other party\'s own insurer to share.',
        detail: 'Two separate promises in one phrase. Without it, the "other insurance" clause makes both policies contribute, which is exactly what an upstream party is trying to avoid. It has to be endorsed on — CG 20 01 or carrier wording — and a certificate saying so does not create it.',
        see: ['other-insurance', 'additional-insured', 'contribution', 'coi'],
    },
    {
        id: 'other-insurance',
        term: 'Other insurance clause',
        category: 'liability',
        def: 'The condition that decides how a policy behaves when another policy covers the same loss — pay its share, sit excess, or contribute by equal shares.',
        detail: 'When two policies both say they are excess to the other, courts usually find the clauses mutually repugnant and make them share anyway. It is the mechanism P&NC endorsements are written to override.',
        see: ['primary-and-noncontributory', 'contribution', 'excess-liability'],
    },
    {
        id: 'waiver-of-subrogation',
        term: 'Waiver of subrogation',
        category: 'liability',
        essential: true,
        def: 'The insured\'s carrier gives up its right to go after a named party after paying a claim — so that party cannot be pursued even if they caused the loss.',
        detail: 'It must be endorsed on the policy, and most forms require the waiver to be agreed in writing before the loss. Waiving without the carrier\'s agreement can prejudice the insured\'s own coverage. On workers\' comp it is a specific endorsement and is separately rated.',
        confused: 'A waiver of subrogation stops the insurer suing. A hold harmless agreement moves liability between the two businesses. Contracts usually demand both, and they do different jobs.',
        see: ['subrogation', 'hold-harmless', 'additional-insured'],
    },
    {
        id: 'hold-harmless',
        term: 'Hold harmless agreement',
        aka: ['indemnification agreement', 'indemnity clause'],
        category: 'practice',
        essential: true,
        def: 'A contract clause where one party agrees to take on another party\'s liability — to defend them, pay their losses, or both.',
        detail: 'Three flavours. Broad form makes you responsible even for the other party\'s sole negligence, and is unenforceable in many states under anti-indemnity statutes. Intermediate form covers their partial negligence. Limited form covers only your own. What the insured signed decides whether their CGL will follow it.',
        see: ['insured-contract', 'contractual-liability', 'waiver-of-subrogation', 'anti-indemnity-statute'],
    },
    {
        id: 'contractual-liability',
        term: 'Contractual liability',
        category: 'liability',
        def: 'Liability the insured takes on by contract rather than by law. The CGL excludes it — then gives most of it back for an "insured contract".',
        see: ['insured-contract', 'hold-harmless', 'exception-to-exclusion'],
    },
    {
        id: 'insured-contract',
        term: 'Insured contract',
        category: 'liability',
        essential: true,
        def: 'The defined list of agreements whose assumed liability a CGL will cover — leases, sidetrack agreements, easements, elevator maintenance, and the big one: the part of any contract where the insured assumes another\'s tort liability for bodily injury or property damage.',
        detail: 'This definition is what makes a hold harmless clause insurable. Assume liability outside it — a promise to cover pure economic loss, or to indemnify for the other party\'s sole negligence in a state where that is enforceable — and the insured is on their own.',
        see: ['contractual-liability', 'hold-harmless', 'definitions-section'],
    },
    {
        id: 'anti-indemnity-statute',
        term: 'Anti-indemnity statute',
        category: 'regulatory',
        def: 'A state law limiting how far one party in a construction contract can be made to indemnify another — usually voiding an agreement to cover the other party\'s sole negligence.',
        detail: 'They vary a great deal by state, and some also restrict additional insured requirements. A clause that is standard in one state is unenforceable next door.',
        see: ['hold-harmless', 'additional-insured'],
    },
    {
        id: 'severability',
        term: 'Severability of interests',
        aka: ['separation of insureds'],
        category: 'liability',
        def: 'The policy applies separately to each insured, as if each held their own policy — so one insured\'s conduct does not automatically wreck coverage for the others.',
        detail: 'It does not increase the limit, and it does not undo exclusions that are drafted to apply to "any insured" rather than "the insured". That word choice is where the argument happens.',
        see: ['insured', 'cross-liability'],
    },
    {
        id: 'cross-liability',
        term: 'Cross-liability',
        category: 'liability',
        def: 'One insured under a policy suing another insured under the same policy.',
        detail: 'Standard CGL wording contemplates it via severability. Many other forms — D&O, some property programmes — exclude it outright.',
        see: ['severability', 'insured-vs-insured'],
    },
    {
        id: 'your-work',
        term: 'Your work exclusion',
        category: 'liability',
        essential: true,
        def: 'A CGL exclusion for damage to the insured\'s own completed work. The policy covers damage their work causes to other things — not the cost of fixing the work itself.',
        detail: 'The critical carveback: the exclusion does not apply if the damaged work, or the work that caused the damage, was performed by a subcontractor. That single sentence is the basis of general contractor coverage, and removing it — the "subcontractor exception" being deleted by endorsement — guts a GC\'s policy.',
        confused: 'Your work is completed work. Your product is a manufactured thing. Damage to work in progress falls under a different exclusion again.',
        see: ['your-product', 'products-completed-operations', 'exception-to-exclusion', 'faulty-workmanship'],
    },
    {
        id: 'your-product',
        term: 'Your product exclusion',
        category: 'liability',
        def: 'A CGL exclusion for damage to the insured\'s own product. Damage the product causes to other property remains covered.',
        see: ['your-work', 'products-completed-operations', 'product-recall'],
    },
    {
        id: 'faulty-workmanship',
        term: 'Faulty workmanship',
        category: 'liability',
        def: 'Poor-quality work. Whether the resulting damage is an "occurrence" at all is one of the most litigated questions in commercial insurance, and the answer differs by state.',
        detail: 'The practical version: a CGL is not a performance bond. It may pay for damage the bad work causes to other property, but it will not pay to redo the work.',
        see: ['your-work', 'occurrence', 'performance-bond'],
    },
    {
        id: 'care-custody-control',
        term: 'Care, custody or control',
        short: 'CCC',
        category: 'liability',
        def: 'A CGL exclusion for damage to property the insured is looking after, renting, or working on.',
        detail: 'It is why a repair shop, a mover, a cleaner and an installer all need something beyond a CGL. Bailee coverage, garagekeepers, or an installation floater fills it depending on the trade.',
        see: ['personal-property-of-others', 'garagekeepers', 'bailee-coverage'],
    },
    {
        id: 'bailee-coverage',
        term: 'Bailee coverage',
        category: 'property',
        def: 'Inland marine coverage for customers\' property in the insured\'s possession — at a repair shop, a laundry, a processor, a warehouse.',
        see: ['care-custody-control', 'personal-property-of-others'],
    },
    {
        id: 'absolute-pollution-exclusion',
        term: 'Absolute pollution exclusion',
        aka: ['total pollution exclusion'],
        category: 'liability',
        def: 'CGL wording removing essentially all pollution liability, including clean-up costs and government orders.',
        detail: 'Its breadth catches things nobody thinks of as pollution: carbon monoxide from a faulty heater, fumes from a floor sealant, silica dust. The narrower "hostile fire" and "building heating equipment" exceptions are worth knowing on habitational and contracting accounts.',
        see: ['pollution-liability', 'exclusion'],
    },
    {
        id: 'xcu',
        term: 'XCU exclusions',
        category: 'liability',
        def: 'Explosion, Collapse and Underground property damage — three exclusions applied to some contracting classes, most often excavation, blasting and utility work.',
        detail: 'Their presence should be checked on any contractor who digs. A general contractor requiring "XCU included" from subs is asking whether these exclusions have been removed.',
        see: ['exclusion', 'cgl'],
    },
    {
        id: 'action-over',
        term: 'Action over claim',
        category: 'liability',
        def: 'An injured employee sues a third party — the property owner or general contractor — who then turns round and claims against the employer under the contract.',
        detail: 'The employer\'s comp policy bars the direct suit, but Part Two employers liability is what responds to the action over. Some markets add an "action over exclusion", which is a serious gap on any construction account, and it is what New York labour law claims run on.',
        see: ['employers-liability', 'exclusive-remedy', 'hold-harmless'],
    },
    {
        id: 'wrap-up',
        term: 'Wrap-up',
        aka: ['ocip', 'ccip', 'controlled insurance program'],
        category: 'liability',
        def: 'One insurance programme covering every contractor on a large project, arranged by the owner (OCIP) or the general contractor (CCIP).',
        detail: 'Subcontractors inside a wrap must have their own policies endorsed to exclude wrapped work, or they pay twice — and their own carrier will usually credit the payroll. Coverage for completed operations after the wrap expires is the classic gap.',
        see: ['additional-insured', 'products-completed-operations'],
    },
    {
        id: 'assault-and-battery',
        term: 'Assault and battery exclusion',
        category: 'liability',
        def: 'Removes liability for fights, attacks and the security response to them — standard on bars, nightclubs, habitational and some retail.',
        detail: 'Often sold back with a sublimit. Watch how it is worded: an exclusion applying to claims "arising out of" an assault also removes negligent-security and negligent-hiring claims, which is the whole exposure.',
        see: ['liquor-liability', 'exclusion'],
    },
    {
        id: 'continuous-trigger',
        term: 'Continuous trigger',
        aka: ['triple trigger', 'injury in fact'],
        category: 'liability',
        def: 'A rule for damage that develops over years — construction defect, asbestos, pollution — under which every policy in force from first exposure to manifestation is triggered.',
        detail: 'Which trigger theory a state uses decides how many policy years pay and how the loss is allocated between them. It is the reason old expired policies still matter and old dec pages are worth keeping forever.',
        see: ['occurrence-form', 'stacking', 'anti-stacking', 'products-completed-operations'],
    },
    {
        id: 'batch-clause',
        term: 'Batch clause',
        aka: ['integrated occurrence'],
        category: 'liability',
        def: 'Wording that treats all claims from one defective batch, lot or production run as a single occurrence, with one limit and one deductible.',
        detail: 'It cuts both ways. One deductible instead of five hundred is welcome; one occurrence limit across five hundred claims is not. Which side it helps depends entirely on the size of the loss.',
        see: ['occurrence-limit', 'products-completed-operations', 'product-recall'],
    },
    {
        id: 'media-liability',
        term: 'Media liability',
        category: 'professional',
        def: 'Covers claims arising from published content — defamation, copyright and trademark infringement, invasion of privacy, and misappropriation of ideas.',
        detail: 'The CGL\'s personal and advertising injury covers a slice of this for a business advertising its own goods. A business whose product *is* content — a publisher, an agency, a broadcaster — needs the standalone form.',
        see: ['coverage-b', 'professional-liability', 'cyber-liability'],
    },
    {
        id: 'duty-to-defend',
        term: 'Duty to defend',
        category: 'claims',
        essential: true,
        def: 'The carrier\'s obligation to provide and pay for a lawyer, triggered by allegations that could possibly be covered — a far lower bar than actually owing payment.',
        detail: 'It is broader than the duty to indemnify: a groundless suit still has to be defended. On a duty-to-defend policy the carrier picks counsel; on an indemnity-only or SIR policy the insured does.',
        confused: 'Duty to defend is about the allegations. Duty to indemnify is about the facts once they are established.',
        see: ['duty-to-indemnify', 'reservation-of-rights', 'panel-counsel', 'sir'],
    },
    {
        id: 'duty-to-indemnify',
        term: 'Duty to indemnify',
        category: 'claims',
        def: 'The carrier\'s obligation to actually pay a judgment or settlement, decided on what really happened rather than what was alleged.',
        see: ['duty-to-defend', 'reservation-of-rights'],
    },
    {
        id: 'panel-counsel',
        term: 'Panel counsel',
        category: 'claims',
        def: 'The list of law firms a carrier will pay for. On most professional, cyber and D&O policies the insured must use one unless the carrier consents otherwise.',
        see: ['duty-to-defend', 'breach-response'],
    },
    {
        id: 'consent-to-settle',
        term: 'Consent to settle',
        aka: ['hammer clause'],
        category: 'claims',
        def: 'A clause letting the insured refuse a settlement the carrier wants to make — usually to protect a professional reputation.',
        detail: 'The hammer clause is the counterweight: refuse, and the carrier\'s liability is capped at what the settlement would have cost, leaving the insured to fund the rest. A "soft hammer" splits the excess, commonly 50/50 or 80/20.',
        see: ['professional-liability', 'settlement'],
    },
];
